import { useSafeState } from 'ahooks';
import React, { useRef, useEffect } from 'react';
import { TextArea } from 'tea-component';

interface AutoResizeTextAreaProps {
  /** 输入框的值 */
  value?: string;
  /** 值变更回调函数 */
  onChange?: (value: string) => void;
  /** 占位文本 */
  placeholder?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 是否已保存状态，用于控制是否恢复原值 */
  isSave?: boolean;
  index: number;
  records: any;
  recordKey: string;
  setRecords: (records: any) => void;
  highlightCnMap?: any;
}

const AutoResizeTextArea: React.FC<AutoResizeTextAreaProps> = (props) => {
  const { value, onChange, placeholder, style, isSave, index, recordKey, records, setRecords, highlightCnMap} = props;
  
  // 获取TextArea的DOM引用，用于高度调整
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  
  // 输入框的受控状态值
  const [text, setText] = useSafeState(value);

  /**
   * 自动调整输入框高度的方法
   * @param element - 需要调整的文本域元素
   */
  const adjustHeight = (element: HTMLTextAreaElement) => {
    // 重置高度以获得正确的scrollHeight
    element.style.height = 'auto';
    // 根据内容设置高度
    element.style.height = `${element.scrollHeight}px`;
  };

  // 监听外部value值变化（用于同步父组件传值）
  useEffect(() => {
    // 当外部传入的value变化时更新本地状态
    setText(value);
  }, [value]);

  // 监听保存状态变化
  useEffect(() => {
    // 当保存未成功时恢复原始值
    if (!isSave) {
      setText(value);
    }
  }, [isSave, value]);

  // 每次text值变化时调整高度
  useEffect(() => {
    if (textAreaRef.current) {
      adjustHeight(textAreaRef.current);
    }
  }, [text,value]);

  useEffect(() => {
    if (value) {
      // 过滤掉 <b> 和 </b> 标签
      const sanitizedValue = value.replace(/<\/?b>/g, '');
      setText(sanitizedValue);
    }
  }, [value]);

  const handleChange = (inputValue: string) => {
    let updatedValue = inputValue;
  
    // 只有当 recordKey 为 'highlight' 时，才进行中英文转换
    if (recordKey === "highlight") {
      const matchedKey = Object.keys(highlightCnMap)?.find(
        (key) => highlightCnMap[key] === inputValue
      );
  
      if (matchedKey) {
        updatedValue = matchedKey;
      }
    }
  
    setText(updatedValue);
    onChange?.(updatedValue);
  
    // 更新 records
    const updatedRecords = records?.map((record: any) =>
      record.index === index ? { ...record, [recordKey]: updatedValue } : record
    );
    setRecords(updatedRecords);
  };

  /**
   * 输入时实时调整高度
   * @param e - 输入事件
   */
  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    adjustHeight(e.currentTarget);
  };

  /**
   * 失焦时处理数据格式化
   * @param e - 失焦事件
   */
  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // 去除首尾空白字符
    const trimmedValue = e.currentTarget.value?.trim();
    // 通过回调通知父组件值变化
    onChange?.(trimmedValue);
    // 更新本地状态
    setText(trimmedValue);
  };

  return (
    <TextArea
      ref={textAreaRef}
      style={{
        width: '595px',
        minHeight: '64px',
        resize: 'none',          // 禁用手动调整大小
        overflow: 'hidden',      // 隐藏滚动条
        ...style,                // 合并自定义样式
      }}
      placeholder={placeholder}
      value={text}
      onChange={handleChange}
      onInput={handleInput}
      onBlur={handleBlur}
    />
  );
};

export default AutoResizeTextArea;